import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateMyProfileApi } from "../api/usersApi";
import {
  DEFAULT_DENSITY,
  MOBILE_MEDIA_QUERY,
  isUiDensity,
  type UiDensity,
} from "../constants/density";
import type { AppDispatch, RootState } from "../store";
import { setUser } from "../store/features/userSlice";
import i18n from "../translations/i18n";
import type translations from "../translations/translations.json";
import {
  type DensityPreference,
  readDensityPreference,
  writeDensityPreference,
} from "../utils/densityStorage";
import { toastEmitter } from "../utils/toastEmitter";
import type { User } from "../types/user";

type TranslationKeys = keyof typeof translations.en;

interface DensityContextValue {
  preference: DensityPreference;
  density: UiDensity;
  isMobileViewport: boolean;
  setDensityForCurrentViewport: (level: UiDensity) => void;
  savePreference: (next: DensityPreference) => Promise<void>;
  saving: boolean;
}

const DensityContext = createContext<DensityContextValue | undefined>(undefined);

const preferenceFromUser = (user: User): DensityPreference => ({
  desktop: isUiDensity(user.uiDensityDesktop) ? user.uiDensityDesktop : DEFAULT_DENSITY,
  mobile: isUiDensity(user.uiDensityMobile) ? user.uiDensityMobile : DEFAULT_DENSITY,
});

const preferencesEqual = (a: DensityPreference, b: DensityPreference): boolean =>
  a.desktop === b.desktop && a.mobile === b.mobile;

export const DensityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<DensityPreference>(() =>
    readDensityPreference(),
  );
  const isMobileViewport = useMediaQuery(MOBILE_MEDIA_QUERY);
  const density = isMobileViewport ? preference.mobile : preference.desktop;
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    const fromServer = preferenceFromUser(user);
    setPreferenceState((current) => (preferencesEqual(current, fromServer) ? current : fromServer));
    writeDensityPreference(fromServer);
  }, [user]);

  const savePreference = useCallback(
    async (next: DensityPreference) => {
      if (!user) {
        return;
      }

      const previous = preference;
      setPreferenceState(next);
      writeDensityPreference(next);
      setSaving(true);

      try {
        const updated = await updateMyProfileApi({
          uiDensityDesktop: next.desktop,
          uiDensityMobile: next.mobile,
        });
        dispatch(setUser(updated));
      } catch {
        setPreferenceState(previous);
        writeDensityPreference(previous);
        toastEmitter.emit(i18n.t("profile.density.saveFailed" as TranslationKeys), "error");
        throw new Error("Failed to save density preference");
      } finally {
        setSaving(false);
      }
    },
    [user, preference, dispatch],
  );

  const setDensityForCurrentViewport = useCallback(
    (level: UiDensity) => {
      const next: DensityPreference = isMobileViewport
        ? { ...preference, mobile: level }
        : { ...preference, desktop: level };
      void savePreference(next).catch(() => {});
    },
    [isMobileViewport, preference, savePreference],
  );

  return (
    <DensityContext.Provider
      value={useMemo(
        () => ({
          preference,
          density,
          isMobileViewport,
          setDensityForCurrentViewport,
          savePreference,
          saving,
        }),
        [
          preference,
          density,
          isMobileViewport,
          setDensityForCurrentViewport,
          savePreference,
          saving,
        ],
      )}
    >
      {children}
    </DensityContext.Provider>
  );
};

export const useDensity = (): DensityContextValue => {
  const ctx = useContext(DensityContext);
  if (!ctx) {
    throw new Error("useDensity must be used within DensityProvider");
  }
  return ctx;
};
